import React, { useEffect, useState } from "react";
import { getDatabase, ref, get, update, onValue } from "firebase/database";
import "./AdminReceive.css";

function AdminReceive({ fgNumber, nodeName, onBack }) {
  const db = getDatabase();
  const [title, setTitle] = useState("");
  
  const [editRemark, setEditRemark] = useState(""); 
  const [otherText, setOtherText] = useState("");
  const [otherClicked, setOtherClicked] = useState(false);
  const [selectedFG, setSelectedFG] = useState("");
  const [showManualFG, setShowManualFG] = useState(false);
  const [manualFG, setManualFG] = useState("");
  const [fgOptions, setFgOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoomImage, setZoomImage] = useState(null);
  const [form, setForm] = useState({
    doNumber: "",
    labelType: "",
    quantity: "",
    manufacturingDate: "",
    receivingDate: "",
    receivingTime: "",
    note: "",
    userName: "",
  });
  const [notes] = useState(["Rust", "Grease", "Dirty", "Other"]);
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [codeList, setCodeList] = useState([]);
  const [images, setImages] = useState({ checklistImageUrl: "", qrImageUrl: "" });
  const [isDeleting, setIsDeleting] = useState(false);
  const [issue, setIssue] = useState({
    message: "",
    reportedBy: "",
    reportedTime: "",
    status: "open"
  });

  // ------------------ Date / Time helpers ------------------
  const normalizeDate = (val) => {
    if (!val) return "";
    const [day, month, year] = val.split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  const displayDate = (val) => {
    if (!val) return "";
    const [year, month, day] = val.split("-");
    return `${day}/${month}/${year}`;
  };

  const normalizeTime = (val) => {
    if (!val) return "";
    const parts = val.split(" ");
    const time = parts[0];
    const modifier = parts[1];
    let [hours, minutes] = time.split(":");
    hours = parseInt(hours);
    if (modifier?.toLowerCase() === "pm" && hours < 12) hours += 12;
    if (modifier?.toLowerCase() === "am" && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  const displayTime = (val) => {
    if (!val) return "";
    let [hours, minutes] = val.split(":");
    hours = parseInt(hours);
    const modifier = hours >= 12 ? "pm" : "am";
    hours = hours % 12 || 12;
    return `${hours.toString().padStart(2, "0")}:${minutes} ${modifier}`;
  };

  // ------------------ Fetch Data ------------------
  useEffect(() => {
    if (!fgNumber || !nodeName) return;
    setTitle(`${nodeName} for ${fgNumber}`);

    const nodeRef = ref(db, `${nodeName}/${fgNumber}`);

    const fetchData = async () => {
      try {
        const snap = await get(nodeRef);
        if (snap.exists()) {
          const data = snap.val();
          setForm({
            doNumber: data.doNumber || "",
            labelType: data.labelType || "",
            quantity: data.quantity || "",
            manufacturingDate: normalizeDate(data.manufacturingDate),
            receivingDate: normalizeDate(data.receivingDate),
            receivingTime: normalizeTime(data.receivingTime),
            note: data.note || "",
            userName: data.userName || "",
          });

          setImages({
            checklistImageUrl: data.checklistImageUrl || "",
            qrImageUrl: data.qrImageUrl || "",
          });

          if (data.note) {
            const arr = data.note.split(",").map((n) => n.trim());
            const predefined = arr.filter((n) => n !== "" && !n.startsWith("Other:"));
            const otherEntry = arr.find((n) => n.startsWith("Other:"));
            if (otherEntry) {
              setSelectedNotes([...predefined, "Other"]);
              setOtherText(otherEntry.replace("Other:", "").trim());
              setOtherClicked(true);
            } else {
              setSelectedNotes(predefined);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    get(ref(db, "Receive")).then((snap) => {
      if (snap.exists()) {
        const allData = snap.val();
        const now = new Date();
        const localDate = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
        const todayStr = displayDate(localDate); 
        const todayFGs = Object.keys(allData).filter(key => allData[key].receivingDate === todayStr);
        setFgOptions(todayFGs);
      }
    });

    get(ref(db, "Products")).then((snap) => {
      const tempCodes = [];
      snap.forEach((child) => {
        if (child.val().code) tempCodes.push(child.val().code);
      });
      setCodeList(tempCodes);
    });

    get(ref(db, "Staff")).then((snap) => {
      const tempStaff = [];
      snap.forEach((child) => {
        const name = child.child("name").val();
        if (name) tempStaff.push(name);
      });
      setStaffList(tempStaff);
    });
  }, [fgNumber, nodeName, db]);

  useEffect(() => {
    if (!fgNumber) return;
    const issueRef = ref(db, `Receive/${fgNumber}/issue`);
    const unsubscribe = onValue(issueRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setIssue(data);
        setEditRemark(data.remark || "");
      }
    });
    return () => unsubscribe();
  }, [fgNumber, db]);

  // ------------------ Save Handler ------------------
  const handleSave = async () => {
    const fgToSave = showManualFG ? manualFG.trim() : (selectedFG !== "Other" ? selectedFG : "");
    const timestamp = Date.now();
    const mainReceivePath = `Receive/${fgNumber}`;
    const progressPath = `Progress/${fgNumber}/s1`;

    const noteToSave = selectedNotes
      .map(n => n === "Other" ? `Other: ${otherText}` : n)
      .join(", ");

    const updates = {
      [`${mainReceivePath}/doNumber`]: form.doNumber || null,
      [`${mainReceivePath}/labelType`]: form.labelType || null,
      [`${mainReceivePath}/quantity`]: form.quantity || null,
      [`${mainReceivePath}/manufacturingDate`]: displayDate(form.manufacturingDate) || null,
      [`${mainReceivePath}/receivingDate`]: displayDate(form.receivingDate) || null,
      [`${mainReceivePath}/receivingTime`]: displayTime(form.receivingTime) || null,
      [`${mainReceivePath}/includeWithFG`]: fgToSave || null,
      [`${mainReceivePath}/userName`]: form.userName || null,
      [`${mainReceivePath}/note`]: noteToSave || null,
      [`${progressPath}/labelType`]: form.labelType || null,
      [`${progressPath}/quantity`]: form.quantity || null,
      [`${progressPath}/receivingDate`]: displayDate(form.receivingDate) || null,
      [`${progressPath}/receivingTime`]: displayTime(form.receivingTime) || null,
    };

    if (editRemark.trim()) {
      updates[`${mainReceivePath}/issue/status`] = "closed";
      updates[`${mainReceivePath}/issue/remark`] = editRemark.trim();
      updates[`${mainReceivePath}/issue/resolvedBy`] = form.userName;
      updates[`${mainReceivePath}/issue/resolvedTime`] = timestamp;
    }

    try {
      await update(ref(db), updates);
      if (fgToSave) {
        const linkedUpdates = {
          [`Receive/${fgToSave}/includeWithFG`]: fgNumber,
        };
        await update(ref(db), linkedUpdates);
      }
      alert("Updated successfully!");
      onBack && onBack();
    } catch (err) {
      alert(`Update failed: ${err.message}`);
    }
  };

  // ------------------ Note Handlers ------------------
  const handleNoteSelect = (note) => {
    if (note === "Other") {
      setOtherClicked(!otherClicked);
      setSelectedNotes(prev => prev.includes("Other") ? prev.filter(n => n !== "Other") : [...prev, "Other"]);
    } else {
      setSelectedNotes(prev => prev.includes(note) ? prev.filter(n => n !== note) : [...prev, note]);
    }
  };

  const handleDeleteNote = () => {
    if (window.confirm("Are you sure you want to delete all notes?")) {
      setSelectedNotes([]);
      setOtherText("");
      setOtherClicked(false);
    }
  };

  const handleDeleteFG = async () => {
    if (!window.confirm(`Delete all records for ${fgNumber}?`)) return;
    setIsDeleting(true);
    try {
      const updates = { [`Receive/${fgNumber}`]: null, [`Progress/${fgNumber}`]: null };
      await update(ref(db), updates);
      setTimeout(() => onBack && onBack(), 1000);
    } catch (err) {
      alert(`Error deleting: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatTime = (ts) => ts ? new Date(ts).toLocaleString() : "-";

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <div className="admin-receive-container">
        <h2 className="title">{title}</h2>

        {issue?.message && (
          <div className="issue-card">
            <div className="issue-header">
              <div className="issue-title">⚠ Issue Reported</div>
              <span className={`issue-status ${issue.status}`}>{issue.status?.toUpperCase()}</span>
            </div>
            <div className="issue-message">{issue.message}</div>
            <div className="issue-meta">
              <span>👤 {issue.reportedBy}</span>
              <span>🕒 {formatTime(issue.reportedTime)}</span>
            </div>
            {issue.status === "closed" && (
              <div className="issue-resolved-info">
                Resolved by: {issue.resolvedBy} | {formatTime(issue.resolvedTime)}
              </div>
            )}
            <textarea
              className="remark-editor"
              value={editRemark}
              placeholder="Enter admin remark"
              onChange={(e) => setEditRemark(e.target.value)}
            />
          </div>
        )}

        <button className="delete-fg-btn" onClick={handleDeleteFG}>Delete Receive 🗑</button>

        <div className="image-section">
          <div className="img-box" onClick={() => setZoomImage(images.qrImageUrl)}>
            <img src={images.qrImageUrl} alt="QR" />
            <span>QR Code</span>
          </div>
          <div className="img-box" onClick={() => setZoomImage(images.checklistImageUrl)}>
            <img src={images.checklistImageUrl} alt="Checklist" />
            <span>Checklist</span>
          </div>
        </div>

        <div className="form-container">
          <div className="form-row">
            <label>DO Number:</label>
            <input type="text" value={form.doNumber} onChange={(e) => setForm({ ...form, doNumber: e.target.value })} />
          </div>

          <div className="form-row">
            <label>Label Type:</label>
            <select value={form.labelType} onChange={(e) => setForm({ ...form, labelType: e.target.value })}>
              <option value="">Select Label</option>
              {codeList.map((c, i) => <option key={i} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="form-row">
            <label>Quantity:</label>
            <input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
          </div>

          <div className="form-row">
            <label>Manufacturing Date:</label>
            <input type="date" value={form.manufacturingDate} onChange={(e) => setForm({ ...form, manufacturingDate: e.target.value })} />
          </div>

          <div className="form-row">
            <label>Receiving Date:</label>
            <input type="date" value={form.receivingDate} onChange={(e) => setForm({ ...form, receivingDate: e.target.value })} />
          </div>

          <div className="form-row">
            <label>Receiving Time:</label>
            <input type="time" value={form.receivingTime} onChange={(e) => setForm({ ...form, receivingTime: e.target.value })} />
          </div>

          <div className="form-row">
            <label>Include with other FG:</label>
            <div className="form-inline">
              <select value={selectedFG} onChange={(e) => { setSelectedFG(e.target.value); setShowManualFG(e.target.value === "Other"); }}>
                <option value="">Select FG</option>
                {fgOptions.map((fg) => <option key={fg} value={fg}>{fg}</option>)}
                <option value="Other">Other (Manual)</option>
              </select>
              {showManualFG && <input type="text" placeholder="FG Number" value={manualFG} onChange={(e) => setManualFG(e.target.value)} />}
            </div>
          </div>

          <div className="form-row">
            <label>Notes:</label>
            <div className="note-section">
              <div className="note-options">
                {notes.map((note, i) => (
                  <button key={i} className={selectedNotes.includes(note) ? "selected" : ""} onClick={() => handleNoteSelect(note)}>{note}</button>
                ))}
                <button className="delete" onClick={handleDeleteNote}>🗑 Clear</button>
              </div>
              {selectedNotes.includes("Other") && (
                <input type="text" className="other-input" placeholder="Specify..." value={otherText} onChange={(e) => setOtherText(e.target.value)} />
              )}
            </div>
          </div>

          <div className="form-row">
            <label>Handled By:</label>
            <select value={form.userName} onChange={(e) => setForm({ ...form, userName: e.target.value })}>
              <option value="">Select User</option>
              {staffList.map((name, i) => <option key={i} value={name}>{name}</option>)}
            </select>
          </div>

          <div className="form-actions">
            <button className="btn-back" onClick={onBack}>⬅ Back</button>
            <button className="btn-save" onClick={handleSave}>💾 Save</button>
          </div>
        </div>
      </div>
      {zoomImage && (
        <div className="image-zoom-overlay" onClick={() => setZoomImage(null)}>
          <div className="image-zoom-content"><img src={zoomImage} alt="Zoomed" /></div>
        </div>
      )}
      {isDeleting && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Deleting...</p>
        </div>
      )}
    </>
  );
}

export default AdminReceive;